// Pure string utilities (ported from legacy `composables/format.js` L748–773).
// The Turkish-aware case-fold search is deliberate and on the contract §17 parity
// list — retyped, behaviour preserved 1:1.

/** Turkish characters → their ASCII fold, so TR text is searchable with plain input. */
const trMap: Record<string, string> = {
  ç: 'c',
  Ç: 'c',
  ğ: 'g',
  Ğ: 'g',
  ı: 'i',
  I: 'i',
  İ: 'i',
  ö: 'o',
  Ö: 'o',
  ş: 's',
  Ş: 's',
  ü: 'u',
  Ü: 'u',
}

/** Fold Turkish characters to ASCII and lowercase, for accent-insensitive comparison. */
export const foldSearchText = (val: unknown): string =>
  String(val ?? '')
    .normalize('NFKC')
    .replace(/[çÇğĞıIiİöÖşŞüÜ]/g, (ch) => trMap[ch] ?? ch)
    .toLowerCase()

/**
 * Collation locale for sorting BACKEND DATA — campaign names, segment labels, metric labels.
 *
 * Deliberately Turkish, and deliberately NOT the UI locale (decided in ORP-19870). The interface is
 * English-only, but the data is not: the backend returns Turkish campaign, day, month and segment
 * names, which is the whole reason `foldSearchText` above exists. Turkish collation treats
 * `ç ğ ı ö ş ü` as distinct letters, so `Çanakkale` sorts after every C word, while `'en'` folds
 * them and interleaves the two — measured, and the orders differ even at `sensitivity: 'base'`
 * (`c` vs `ç` compares `-1` under `tr`, `0` under `en`).
 *
 * One owner instead of three scattered literals, and overridable per call — `sortRows`' `options.locale`,
 * `uniqueMetrics`/`uniqueDimensions`' `locale` argument. Changing this constant moves the product
 * default and visibly reorders every Turkish account's lists, so it is a product call, not a cleanup.
 */
export const DATA_COLLATION_LOCALE = 'tr'

/** Convert camelCase / spaced / hyphenated text to `snake_case`. */
export const toSnakeCase = (str: string): string =>
  String(str)
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    .replace(/[\s-]+/g, '_')
    .toLowerCase()

/**
 * Turkish-fold substring search: `strIncludes('Şubat', 'subat') === true`. Both
 * operands are folded to ASCII and lowercased before the `includes` check.
 */
export const strIncludes = (str: string, query: string): boolean =>
  foldSearchText(str).includes(foldSearchText(query))
